import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { css, cx } from '@linaria/core'
import { styled } from '@linaria/react'

import { RoundButton } from 'components/designSystem/RoundButton/RoundButton'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { ArrowLeftSvg } from 'components/designSystem/svgr/ArrowLeftSvg'
import { ArrowRightSvg } from 'components/designSystem/svgr/ArrowRightSvg'
import { Direction } from 'components/page/RatingWave/Rating.types'
import { useModalOpened } from 'hooks/useModalOpened'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { ProfileNextContext } from '../../ProfileMain/context/ProfileNextContext'

/**
 *
 * Дорабатывает с дизайнерами на ходу
 * механизм переключение фото для desktop
 *
 */
export const PhotosSlide: FC<PropsWithChildren> = ({ children }) => {
  const hasModal = useModalOpened()
  const { enable } = useContext(ProfileNextContext)
  const { rtl } = useShallowEqualSelector(({ systemReducer: { rtl } }) => ({
    rtl,
  }))
  const [currentIndex, setCurrentIndex] = useState(1)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const childArray = React.Children.toArray(children)

  const extendedChildren = [
    childArray[childArray.length - 1],
    ...childArray,
    childArray[0],
  ]

  const handleNavigation = useCallback(
    (direction: Direction) => {
      if (isTransitioning) return

      const _direction = rtl ? -1 * direction : direction

      setIsTransitioning(true)
      setCurrentIndex((prevIndex) => prevIndex + _direction)
    },
    [isTransitioning, rtl]
  )

  const handleTransitionEnd = () => {
    if (currentIndex === 0) {
      setIsTransitioning(false)
      setCurrentIndex(childArray.length)
    } else if (currentIndex === childArray.length + 1) {
      setIsTransitioning(false)
      setCurrentIndex(1)
    } else {
      setIsTransitioning(false)
    }
  }

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Space' && !hasModal && enable) {
        event.preventDefault()
        handleNavigation(rtl ? Direction.left : Direction.right)
      }
    },
    [handleNavigation, hasModal, enable, rtl]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  return (
    <Parent>
      <Body
        currentIndex={currentIndex}
        style={{
          transition: isTransitioning ? 'transform 0.3s ease-in-out' : 'none',
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {extendedChildren.map((child, index) => (
          <Photo key={index}>{child}</Photo>
        ))}
      </Body>

      <Left className={cx(commonCss)} data-name="arrow-left">
        <RoundButton size="S" onClick={() => handleNavigation(Direction.left)}>
          <ArrowLeftSvg width="20" height="20" />
        </RoundButton>
      </Left>
      <Right className={cx(commonCss)} data-name="arrow-right">
        <RoundButton size="S" onClick={() => handleNavigation(Direction.right)}>
          <ArrowRightSvg width="20" height="20" />
        </RoundButton>
      </Right>
    </Parent>
  )
}

const commonCss = css`
  opacity: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: 0.2s;

  @media (min-width: ${breakpoints.mobile + 1}px) and (hover: none) {
    opacity: 1;
  }
`

const Left = styled.div`
  left: 24px;
`

const Right = styled.div`
  right: 24px;
`

const Parent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: row;
  overflow: hidden;
  color: var(--accent-primary-on-accent-primary);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      > ${Left}, > ${Right} {
        opacity: 1;
      }
    }
  }
`

const Body = styled.div<{ currentIndex: number }>`
  display: flex;
  transition: transform 0.3s ease-in-out;
  min-width: 100%;

  html[dir='ltr'] & {
    transform: translateX(calc(${(props) => props.currentIndex * 100} * -1%));
  }

  html[dir='rtl'] & {
    transform: translateX(calc(${(props) => props.currentIndex * 100} * 1%));
  }
`

const Photo = styled.div`
  min-width: 100%;
  height: 100%;
  position: relative;
`
